import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import "./WebsiteSeo.css";

const CONTACT = "/contact?service=Website%20SEO";

const results = [
  ["01", "Website", "Page title", "Description", "Structure"],
  ["02", "Services", "Page title", "Description", "Content"],
  ["03", "Resources", "Page title", "Description", "Relevance"]
];

const pages = ["Services", "About", "Resources", "Contact"];

const checks = [
  ["crawl", "Crawlability", "Ready", "Whether a crawler can reach the pages and follow the paths between them."],
  ["index", "Indexing", "Review", "Which pages are meant to be indexed, and which should stay out of search."],
  ["structure", "Page structure", "Ready", "Headings, landmarks, and the order of the content on the page."],
  ["meta", "Metadata", "Needs attention", "Titles and descriptions that say what a page is actually about."],
  ["speed", "Performance", "Review", "How the page is delivered, including images, scripts, and layout."],
  ["mobile", "Mobile experience", "Ready", "Whether the same page stays readable and usable on a small screen."],
  ["urls", "URLs", "Ready", "Addresses that reflect the page, without a chain of unclear parameters."],
  ["links", "Internal links", "Needs attention", "The links that connect a page to the rest of the site."]
];

const zones = [
  ["topic", "Topic", "The title and heading say what the page is about."],
  ["intent", "Intent", "The content answers the reason someone searched."],
  ["structure", "Structure", "Headings and links give the page an order."],
  ["context", "Context", "Images and metadata add the surrounding meaning."]
];

const zoneMap = {
  topic: ["title", "heading"],
  intent: ["content"],
  structure: ["heading", "links"],
  context: ["images", "meta"]
};

const intents = [
  ["question", "Question", "Someone is trying to understand a topic.", ["Guide", "FAQ"]],
  ["comparison", "Comparison", "Someone is weighing ways to solve a problem.", ["Guide", "Resource"]],
  ["action", "Action", "Someone is ready to find a service.", ["Service Page"]]
];

const contentTypes = ["Service Page", "Guide", "FAQ", "Resource"];

const signals = [
  ["load", "Loading", "How the page begins to appear."],
  ["images", "Images", "The weight and placement of pictures."],
  ["scripts", "Scripts", "The code the page has to run."],
  ["layout", "Layout", "Whether the page stays stable as it loads."],
  ["mobile", "Mobile", "How that same page behaves on a phone."]
];

const nodes = [
  ["technical", "Technical", "Crawl, index, and page structure."],
  ["content", "Content", "Pages written for a real search."],
  ["structure", "Structure", "How the site's information is organized."],
  ["links", "Internal links", "How one page points to another."],
  ["local", "Local", "How a nearby search reads the business."],
  ["performance", "Performance", "How the page is delivered."],
  ["measure", "Measurement", "What is watched after a change."]
];

const process = [
  ["Understand", "Learn the business, the audience, and the pages that already exist."],
  ["Audit", "Inspect crawl paths, structure, metadata, links, and performance."],
  ["Prioritize", "Choose the changes that support the pages people should find."],
  ["Optimize", "Revise structure, content, and on-page signals on those pages."],
  ["Publish", "Put the updated pages and content on the live site."],
  ["Measure", "Watch visibility, queries, pages, and technical health over time."],
  ["Improve", "Use what changed to decide the next round of work."]
];

const measures = [
  ["Visibility", "Which pages can be found."],
  ["Content", "Whether a page still matches its topic."],
  ["Pages", "What is published and indexed."],
  ["Queries", "The searches that surface the site."],
  ["Technical health", "The checks that still need attention."]
];

const reasons = [
  ["Technical first", "We look at the structure that supports search visibility.", "tech"],
  ["Content with purpose", "Pages should answer real questions and support real users.", "content"],
  ["Connected to the website", "SEO considerations should work alongside the site's structure, content, performance, and user experience.", "link"],
  ["Ready to improve", "SEO is treated as an ongoing process rather than a one-time configuration.", "loop"]
];

const faqs = [
  ["What does website SEO include?", "It covers the structure of the site, the content on the pages, metadata, internal links, and how those pieces relate to what people search for. The exact scope follows the website."],
  ["Do you work on technical SEO?", "Yes. A technical review looks at crawlability, indexing, page structure, metadata, URLs, internal links, mobile experience, and performance."],
  ["Can you optimize existing website pages?", "Yes. An existing page can be revised so the topic, headings, content, images, links, and metadata make its purpose clearer."],
  ["Can you help with local SEO?", "Yes. Local work covers how the business, its location, services, hours, and contact details are presented. It does not promise a position in a particular place."],
  ["Do you help with SEO content?", "Yes. Content is planned around the reason for the search: a question, a comparison, or a next step. The page should be useful to a person first."],
  ["Can SEO be built into a new website?", "Yes. Structure, templates, metadata, and internal links can be part of a new site from the start, alongside the design and the build."],
  ["How do you measure SEO work?", "By watching search visibility, indexed pages, the queries that surface the site, and technical health over time. The point is to see what changed, not to promise a number."],
  ["Is SEO a one-time project or ongoing work?", "It can start as a defined project, such as an audit and a first set of changes. Search, content, and the website keep moving, so the work continues after that first pass."]
];

function useInView(threshold = 0.22) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setShown(true);
      return undefined;
    }
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setShown(true);
        observer.disconnect();
      }
    }, { threshold, rootMargin: "0px 0px -8% 0px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, shown];
}

function Reveal({ children, className = "", delay = 0 }) {
  const [ref, shown] = useInView();
  return (
    <div ref={ref} className={`se-reveal ${shown ? "is-in" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function Kicker({ index, label }) {
  return (
    <p className="se-kicker">
      <span>{index}</span>
      <span>{label}</span>
    </p>
  );
}

function SearchHero() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || paused) return undefined;
    const id = window.setInterval(() => setActive((current) => (current + 1) % results.length), 2800);
    return () => window.clearInterval(id);
  }, [paused]);
  const signal = results[active][4];
  return (
    <div className="se-search" aria-hidden="true" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="se-search-bar">
        <i />
        <span>Search</span>
      </div>
      <div className="se-signals">
        {["Structure", "Content", "Relevance"].map((label) => (
          <span key={label} className={signal === label ? "is-on" : ""}>{label}</span>
        ))}
      </div>
      <div className="se-results">
        {results.map(([number, title, pageTitle, description], index) => (
          <div key={number} className={`se-result ${active === index ? "is-on" : ""}`} onMouseEnter={() => setActive(index)}>
            <p><span>{number}</span>{title}</p>
            <small>{pageTitle}</small>
            <small>{description}</small>
          </div>
        ))}
      </div>
    </div>
  );
}

function SiteMap() {
  const [page, setPage] = useState("Services");
  return (
    <div className="se-map">
      <div className="se-tree">
        <div className="se-home">Home</div>
        <div className="se-branches">
          {pages.map((label) => (
            <button key={label} type="button" className={page === label ? "is-on" : ""} onMouseEnter={() => setPage(label)} onFocus={() => setPage(label)} onClick={() => setPage(label)}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <ol className="se-chain">
        {["Page", "Content", "Metadata", "Internal links"].map((label, index) => (
          <li key={label} style={{ animationDelay: `${index * 120}ms` }}>
            <em>{String(index + 1).padStart(2, "0")}</em>
            <span>{label}</span>
          </li>
        ))}
      </ol>
      <p className="se-caption">The {page.toLowerCase()} page sits in the structure, with content, metadata, and links back to the rest of the site.</p>
    </div>
  );
}

function AuditSketch({ id }) {
  if (id === "index") {
    return (
      <div className="se-mini se-index">
        <div><b>Home</b><em>Indexed</em></div>
        <div><b>Services</b><em>Indexed</em></div>
        <div className="is-off"><b>Draft</b><em>Not indexed</em></div>
      </div>
    );
  }
  if (id === "structure") {
    return (
      <div className="se-mini se-hier">
        <span className="is-1">Page</span>
        <span className="is-2">Section</span>
        <span className="is-3">Detail</span>
        <span className="is-2">Section</span>
      </div>
    );
  }
  if (id === "meta") {
    return (
      <div className="se-mini se-meta">
        <div><small>Title</small><b>Page title</b></div>
        <div><small>Description</small><b>What this page covers</b></div>
        <div><small>Meta</small><b>Canonical path</b></div>
      </div>
    );
  }
  if (id === "speed") {
    return (
      <div className="se-mini se-res">
        <div><b>Document</b><i /></div>
        <div><b>Image</b><i className="is-mid" /></div>
        <div><b>Script</b><i className="is-short" /></div>
      </div>
    );
  }
  if (id === "mobile") {
    return (
      <div className="se-mini se-devices">
        <div className="se-phone">
          <i />
          <b>Page</b>
          <span />
          <em>Open</em>
        </div>
        <div className="se-wide">
          <b>Page</b>
          <span />
          <span className="is-short" />
        </div>
      </div>
    );
  }
  if (id === "urls") {
    return (
      <ul className="se-mini se-urls">
        <li>/</li>
        <li>/services</li>
        <li>/about</li>
        <li>/contact</li>
      </ul>
    );
  }
  if (id === "links") {
    return (
      <div className="se-mini se-linkmap">
        <span>Services</span>
        <b>Home</b>
        <span>About</span>
        <span>Contact</span>
      </div>
    );
  }
  return (
    <div className="se-mini se-crawl">
      <div className="se-node">Site</div>
      <ul>
        <li>
          <div className="se-node">Home</div>
          <ul>
            <li><span>Services</span></li>
            <li><span>About</span></li>
            <li><span>Contact</span></li>
          </ul>
        </li>
        <li><span>Resources</span></li>
      </ul>
    </div>
  );
}

function AuditPanel() {
  const [active, setActive] = useState("meta");
  const current = checks.find(([id]) => id === active);
  return (
    <div className="se-audit">
      <div className="se-audit-list" role="tablist" aria-label="Technical review areas">
        {checks.map(([id, label, state]) => (
          <button key={id} type="button" role="tab" aria-selected={active === id} className={`se-audit-item is-${state === "Ready" ? "ready" : state === "Review" ? "review" : "attention"} ${active === id ? "is-on" : ""}`} onClick={() => setActive(id)}>
            <span>{label}</span>
            <em>{state}</em>
          </button>
        ))}
      </div>
      <div className="se-audit-detail" role="tabpanel">
        <p className="se-audit-kicker">{current[2]}</p>
        <h3>{current[1]}</h3>
        <p>{current[3]}</p>
        <div key={current[0]} aria-hidden="true">
          <AuditSketch id={current[0]} />
        </div>
        <p className="se-caption">Sample review states. Not a score.</p>
      </div>
    </div>
  );
}

function OnPage() {
  const [zone, setZone] = useState("topic");
  const lit = zoneMap[zone];
  return (
    <div className="se-onpage">
      <div className="se-doc" aria-hidden="true">
        <div className={`se-doc-title ${lit.includes("title") ? "is-lit" : ""}`}>
          <small>Title</small>
          <b>Page title</b>
        </div>
        <div className={`se-doc-heading ${lit.includes("heading") ? "is-lit" : ""}`}>Page heading</div>
        <div className={`se-doc-content ${lit.includes("content") ? "is-lit" : ""}`}>
          <p>A short block about what this page is for and who it helps.</p>
        </div>
        <div className={`se-doc-images ${lit.includes("images") ? "is-lit" : ""}`}>
          <span>Image</span>
        </div>
        <div className={`se-doc-links ${lit.includes("links") ? "is-lit" : ""}`}>
          <span>Services</span><span>Guide</span>
        </div>
        <div className={`se-doc-meta ${lit.includes("meta") ? "is-lit" : ""}`}>
          <small>Metadata</small>
          <b>Description</b>
        </div>
      </div>
      <div className="se-zones">
        {zones.map(([id, label, text]) => (
          <button key={id} type="button" className={zone === id ? "is-on" : ""} onMouseEnter={() => setZone(id)} onFocus={() => setZone(id)} onClick={() => setZone(id)}>
            <strong>{label}</strong>
            <span>{text}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function IntentMap() {
  const [active, setActive] = useState("question");
  const current = intents.find(([id]) => id === active);
  return (
    <div className="se-intent">
      <div className="se-intent-search">Search</div>
      <div className="se-intent-list">
        {intents.map(([id, label, text]) => (
          <button key={id} type="button" className={active === id ? "is-on" : ""} onClick={() => setActive(id)} onMouseEnter={() => setActive(id)} onFocus={() => setActive(id)}>
            <strong>{label}</strong>
            <span>{text}</span>
          </button>
        ))}
      </div>
      <div className="se-types" aria-hidden="true">
        {contentTypes.map((type) => (
          <span key={type} className={current[3].includes(type) ? "is-on" : ""}>{type}</span>
        ))}
      </div>
      <p className="se-caption">Search intent points to a page that is useful for that purpose: {current[3].join(" and ")}.</p>
    </div>
  );
}

function LocalSearch() {
  const rows = [
    ["Business", "The name of the business"],
    ["Location", "Where the work happens"],
    ["Service", "What a nearby search is for"],
    ["Hours", "When someone can reach you"],
    ["Contact", "How to take the next step"]
  ];
  const [active, setActive] = useState(0);
  return (
    <div className="se-local">
      <div className="se-local-grid" aria-hidden="true" />
      <div className="se-local-card">
        <div className="se-search-bar">
          <i />
          <span>service near me</span>
        </div>
        <div className="se-local-rows">
          {rows.map(([label, text], index) => (
            <button key={label} type="button" className={active === index ? "is-on" : ""} onMouseEnter={() => setActive(index)} onFocus={() => setActive(index)} onClick={() => setActive(index)}>
              <strong>{label}</strong>
              <span>{text}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function PerformanceBoard() {
  const [active, setActive] = useState("load");
  return (
    <div className="se-perf">
      <div className={`se-sheet is-${active}`} aria-hidden="true">
        <header className="se-sheet-top">
          <i />
          <span>Page</span>
        </header>
        <div className="se-sheet-body">
          <b>Heading</b>
          <p>Content the page has to present.</p>
          <div className="se-sheet-image">Image</div>
        </div>
        <footer className="se-sheet-foot">
          <span>Script</span>
          <i />
        </footer>
      </div>
      <div className="se-meters">
        {signals.map(([id, label, text]) => (
          <button key={id} type="button" className={active === id ? "is-on" : ""} onMouseEnter={() => setActive(id)} onFocus={() => setActive(id)} onClick={() => setActive(id)}>
            <span>{label}</span>
            <i />
            <small>{text}</small>
          </button>
        ))}
      </div>
    </div>
  );
}

function EcoSystem() {
  const [active, setActive] = useState("technical");
  const current = nodes.find(([id]) => id === active);
  return (
    <div className="se-eco">
      <div className="se-eco-row">
        {nodes.slice(0, 4).map(([id, label]) => (
          <button key={id} type="button" className={active === id ? "is-on" : ""} onMouseEnter={() => setActive(id)} onFocus={() => setActive(id)} onClick={() => setActive(id)}>
            {label}
          </button>
        ))}
      </div>
      <div className="se-eco-core">
        <span>Website</span>
        <p>{current[2]}</p>
      </div>
      <div className="se-eco-row is-lower">
        {nodes.slice(4).map(([id, label]) => (
          <button key={id} type="button" className={active === id ? "is-on" : ""} onMouseEnter={() => setActive(id)} onFocus={() => setActive(id)} onClick={() => setActive(id)}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ProcessViz({ index }) {
  if (index === 0) {
    return (
      <div className="se-viz se-viz-context">
        <div className="se-search-bar"><i /><span>Search</span></div>
        <div><span>Audience</span><span>Pages</span><span>Topics</span></div>
      </div>
    );
  }
  if (index === 1) {
    return (
      <ul className="se-viz se-viz-checks">
        {["Crawl", "Structure", "Metadata", "Links"].map((label) => <li key={label}><i />{label}</li>)}
      </ul>
    );
  }
  if (index === 2) {
    return (
      <div className="se-viz se-viz-matrix">
        <div><small>Now</small><strong>Structure</strong></div>
        <div><small>Next</small><strong>Content</strong></div>
        <div><small>Later</small><strong>Local</strong></div>
      </div>
    );
  }
  if (index === 3) {
    return (
      <div className="se-viz se-viz-page">
        <b>Title</b>
        <span>Heading</span>
        <i /><i />
      </div>
    );
  }
  if (index === 4) {
    return (
      <div className="se-viz se-viz-publish">
        <span>Draft</span>
        <i />
        <span>Review</span>
        <i />
        <span className="is-on">Live</span>
      </div>
    );
  }
  if (index === 5) {
    return (
      <ul className="se-viz se-viz-watch">
        {["Visibility", "Queries", "Pages"].map((label) => <li key={label}><span>{label}</span><b /></li>)}
      </ul>
    );
  }
  return (
    <div className="se-viz se-viz-loop">
      {["Observe", "Adjust", "Review"].map((label) => <span key={label}>{label}</span>)}
    </div>
  );
}

function ProcessTimeline() {
  const [active, setActive] = useState(0);
  const refs = useRef([]);
  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    let observer;
    const bind = () => {
      observer?.disconnect();
      if (desktop.matches || reduced.matches) return;
      const nodesToWatch = refs.current.filter(Boolean);
      observer = new IntersectionObserver((entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(Number(visible.target.dataset.index));
      }, { threshold: [0.6], rootMargin: "-30% 0px -40% 0px" });
      nodesToWatch.forEach((node) => observer.observe(node));
    };
    bind();
    desktop.addEventListener("change", bind);
    return () => {
      desktop.removeEventListener("change", bind);
      observer?.disconnect();
    };
  }, []);
  const [title, text] = process[active];
  return (
    <>
      <div className="se-process" style={{ "--se-fill": `${(active / (process.length - 1)) * 100}%` }}>
        <div className="se-process-rail" />
        <div className="se-process-fill" />
        {process.map(([stepTitle, stepText], index) => (
          <button
            key={stepTitle}
            type="button"
            data-index={index}
            ref={(node) => { refs.current[index] = node; }}
            className={`se-step ${active === index ? "is-active" : ""}`}
            aria-pressed={active === index}
            onClick={() => setActive(index)}
          >
            <span className="se-step-mark">{String(index + 1).padStart(2, "0")}</span>
            <span className="se-step-copy">
              <span>{stepTitle}</span>
              <span>{stepText}</span>
            </span>
          </button>
        ))}
      </div>
      <div className="se-stage" aria-live="polite">
        <div className="se-stage-body" key={active}>
          <p className="se-stage-index">{String(active + 1).padStart(2, "0")}</p>
          <h3>{title}</h3>
          <p className="se-stage-copy">{text}</p>
          <ProcessViz index={active} />
        </div>
      </div>
    </>
  );
}

function MeasureBoard() {
  const [active, setActive] = useState(0);
  return (
    <div className="se-measure">
      <div className="se-measure-list">
        {measures.map(([label, text], index) => (
          <button key={label} type="button" className={active === index ? "is-on" : ""} onMouseEnter={() => setActive(index)} onFocus={() => setActive(index)} onClick={() => setActive(index)}>
            <span>{label}</span>
            <i />
            <small>{text}</small>
          </button>
        ))}
      </div>
      <ol className="se-flow">
        {["Observe", "Understand", "Improve"].map((label, index) => (
          <li key={label}>
            <em>{String(index + 1).padStart(2, "0")}</em>
            {label}
          </li>
        ))}
      </ol>
    </div>
  );
}

function ReasonMark({ kind }) {
  if (kind === "tech") return <div className="se-mark is-tech" aria-hidden="true"><i /><i /><i /></div>;
  if (kind === "content") return <div className="se-mark is-content" aria-hidden="true"><b /><span /><span /></div>;
  if (kind === "link") return <div className="se-mark is-link" aria-hidden="true"><i /><b /><i /></div>;
  return <div className="se-mark is-loop" aria-hidden="true"><span>1</span><span>2</span><span>3</span></div>;
}

function FaqList() {
  const [open, setOpen] = useState(null);
  return (
    <div className="border-t border-slate-200">
      {faqs.map(([question, answer], index) => {
        const isOpen = open === index;
        return (
          <div key={question} className={`se-acc border-b border-slate-200 ${isOpen ? "is-open" : ""}`}>
            <button type="button" className="w-full flex items-center justify-between gap-6 py-3.5 text-left" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : index)}>
              <span className="font-semibold text-slate-900 font-heading">{question}</span>
              <span className="se-plus" aria-hidden="true"><i /><i /></span>
            </button>
            <div className={`se-acc-panel ${isOpen ? "is-open" : ""}`}>
              <div><p className="pb-5 text-slate-600 leading-relaxed">{answer}</p></div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function WebsiteSeo() {
  useEffect(() => {
    const previous = document.title;
    document.title = "Website SEO | BizLogic Developers";
    return () => { document.title = previous; };
  }, []);

  return (
    <article data-testid="service-detail-page" className="se-page bg-white">
      <header className="se-hero border-b border-slate-200">
        <div className="se-wrap grid grid-cols-1 xl:grid-cols-12 gap-8 xl:gap-10 items-center">
          <div className="xl:col-span-6">
            <Link to="/services" data-testid="service-detail-back" className="se-hero-item inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600 mb-5" style={{ animationDelay: "40ms" }}>
              <ArrowLeft className="w-4 h-4" />
              All services
            </Link>
            <p className="se-hero-item se-kicker" style={{ animationDelay: "120ms" }}>
              <span>06</span>
              <span>Website SEO</span>
            </p>
            <h1 className="se-hero-item text-4xl sm:text-5xl xl:text-[3.15rem] font-bold tracking-tight text-slate-900 font-heading leading-[1.05]" style={{ animationDelay: "220ms" }}>
              Make Your Website<span className="hidden xl:inline"><br /></span> Easier to Find.
            </h1>
            <p className="se-hero-item se-lead text-lg text-slate-600 leading-relaxed" style={{ animationDelay: "360ms" }}>
              Technical structure, content, and search strategy designed to help your website become easier for search engines and people to understand.
            </p>
            <div className="se-hero-item se-after flex flex-col sm:flex-row gap-3" style={{ animationDelay: "500ms" }}>
              <Link to={CONTACT} data-testid="service-panel-cta-websiteseo" className="group inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3.5 rounded-xl shadow-md shadow-blue-600/10 transition-all hover:-translate-y-0.5 motion-reduce:transition-none motion-reduce:hover:translate-y-0">
                Start a Project
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0" />
              </Link>
              <Link to="/services" className="group inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-blue-600 text-slate-800 hover:text-blue-600 font-medium px-6 py-3.5 rounded-xl transition-colors">
                Explore Services
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0" />
              </Link>
            </div>
          </div>
          <div className="xl:col-span-6">
            <SearchHero />
          </div>
        </div>
      </header>

      <section className="se-section border-b border-slate-200">
        <div className="se-wrap grid grid-cols-1 xl:grid-cols-2 gap-8 xl:gap-12 items-center">
          <Reveal>
            <Kicker index="01" label="Search foundation" />
            <h2 className="se-title text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Good SEO Starts With a Website Search Engines Can Understand.</h2>
            <p className="se-lead text-slate-600 leading-relaxed">
              Search visibility starts with the structure underneath the page. Clean architecture, accessible content, clear relationships, and technically sound pages give search engines a better understanding of what your website contains.
            </p>
          </Reveal>
          <SiteMap />
        </div>
      </section>

      <section className="se-section border-b border-slate-200 bg-slate-50/70">
        <div className="se-wrap">
          <Reveal>
            <Kicker index="02" label="Technical SEO" />
            <h2 className="se-title text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">The Structure Behind Search Visibility.</h2>
            <p className="se-lead text-slate-600 leading-relaxed">
              A technical review looks at how the site can be crawled, indexed, structured, and used. Each row is a place to look, not a score.
            </p>
          </Reveal>
          <div className="se-after"><AuditPanel /></div>
        </div>
      </section>

      <section className="se-section border-b border-slate-200">
        <div className="se-wrap">
          <Reveal>
            <Kicker index="03" label="On-page SEO" />
            <h2 className="se-title text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Every Page Should Make Its Purpose Clear.</h2>
            <p className="se-lead text-slate-600 leading-relaxed">
              On-page work connects the title, the content, and the surrounding context so a page is clear about what it is for.
            </p>
          </Reveal>
          <div className="se-after"><OnPage /></div>
        </div>
      </section>

      <section className="se-section border-b border-slate-200 bg-[#f7f9fc]">
        <div className="se-wrap">
          <Reveal>
            <Kicker index="04" label="Content and search intent" />
            <h2 className="se-title text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Create Content Around What People Are Looking For.</h2>
            <p className="se-lead text-slate-600 leading-relaxed">
              The page should match the reason for the search: a question, a comparison, or a next step.
            </p>
          </Reveal>
          <div className="se-after"><IntentMap /></div>
        </div>
      </section>

      <section className="se-section border-b border-slate-200">
        <div className="se-wrap grid grid-cols-1 xl:grid-cols-2 gap-8 xl:gap-12 items-center">
          <Reveal>
            <Kicker index="05" label="Local SEO" />
            <h2 className="se-title text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Help Nearby Customers Find the Right Business.</h2>
            <p className="se-lead text-slate-600 leading-relaxed">
              Local search depends on a clear business, a location, the service, and a way to get in touch. It is not a promise of a position in a particular place.
            </p>
          </Reveal>
          <LocalSearch />
        </div>
      </section>

      <section className="se-section border-b border-slate-200 bg-slate-50/70">
        <div className="se-wrap grid grid-cols-1 xl:grid-cols-2 gap-8 xl:gap-12 items-center">
          <Reveal className="xl:order-2">
            <Kicker index="06" label="Site performance" />
            <h2 className="se-title text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Performance Is Part of the Experience.</h2>
            <p className="se-lead text-slate-600 leading-relaxed">
              Loading, images, scripts, layout, and the mobile page are part of the same review. The work is to examine and improve them, not to quote a speed figure.
            </p>
          </Reveal>
          <PerformanceBoard />
        </div>
      </section>

      <section className="se-section border-b border-slate-200">
        <div className="se-wrap">
          <Reveal>
            <Kicker index="07" label="SEO system" />
            <h2 className="se-title text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Every Optimization Connects to Another.</h2>
            <p className="se-lead text-slate-600 leading-relaxed">
              A change in structure, content, or performance reaches the rest of the site. SEO is the system around the website, not a single setting.
            </p>
          </Reveal>
          <div className="se-after"><EcoSystem /></div>
        </div>
      </section>

      <section className="se-section se-build border-b border-slate-200 bg-[#f7f9fc]">
        <div className="se-wrap">
          <div className="se-build-intro">
            <Reveal>
              <Kicker index="08" label="How we work" />
              <h2 className="se-title text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">SEO Is an Ongoing Process.</h2>
              <p className="se-lead text-slate-600 leading-relaxed">
                Select a step. The work moves from understanding the site to the next round of improvement.
              </p>
            </Reveal>
          </div>
          <ProcessTimeline />
        </div>
      </section>

      <section className="se-section border-b border-slate-200">
        <div className="se-wrap">
          <Reveal>
            <Kicker index="09" label="Measurement" />
            <h2 className="se-title text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Know What Changed. Keep Improving.</h2>
            <p className="se-lead text-slate-600 leading-relaxed">
              Measurement is how the next change is chosen. The view below is a way of looking, not a report of results.
            </p>
          </Reveal>
          <div className="se-after"><MeasureBoard /></div>
        </div>
      </section>

      <section className="se-section border-b border-slate-200 bg-slate-50/70">
        <div className="se-wrap">
          <Reveal>
            <Kicker index="10" label="Why BizLogic" />
            <h2 className="se-title text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">SEO Built Into the Website, Not Added as an Afterthought.</h2>
          </Reveal>
          <div className="se-after grid grid-cols-1 md:grid-cols-2 gap-3">
            {reasons.map(([title, text, kind], index) => (
              <Reveal key={title} delay={(index % 2) * 70}>
                <article className="se-reason h-full">
                  <ReasonMark kind={kind} />
                  <h3 className="text-xl font-semibold text-slate-900 font-heading">{title}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed">{text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="se-section border-b border-slate-200">
        <div className="se-wrap">
          <Reveal>
            <Kicker index="11" label="FAQ" />
            <h2 className="se-title text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Questions About Website SEO?</h2>
          </Reveal>
          <div className="se-after"><FaqList /></div>
        </div>
      </section>

      <section className="se-cta">
        <div className="se-cta-grid" />
        <div className="se-cta-glow" />
        <div className="se-wrap relative se-section">
          <h2 className="se-title text-4xl sm:text-5xl font-bold tracking-tight font-heading leading-tight">
            Let&apos;s Make Your Website Easier to Find.
          </h2>
          <p className="se-lead text-slate-300 leading-relaxed">
            Tell us about your website, your audience, and what you want people to find. We&apos;ll help identify where SEO can fit into the bigger picture.
          </p>
          <div className="se-after flex flex-col sm:flex-row gap-3">
            <Link to={CONTACT} className="group inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3.5 rounded-xl transition-all hover:-translate-y-0.5 motion-reduce:transition-none motion-reduce:hover:translate-y-0">
              Start a Project
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0" />
            </Link>
            <Link to="/services" className="group inline-flex items-center justify-center gap-2 border border-slate-600 hover:border-white text-white font-semibold px-6 py-3.5 rounded-xl transition-colors">
              Explore Services
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0" />
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}
